/**
 * BANKER_ROLL (cee-lo) — banker rolls 3 dice for their result.
 *
 * onEnter: exit any leftover betting framing (camera back to default,
 * wallet hidden), set profile mode to shooting, hide lock-in /
 * countdown / instruction. Stash the active roller's id for the
 * onExit handoff comparison. The active roller (banker) awaits the
 * server's dice-authorize signal so onEnter doesn't resolve until the
 * dice are spawned and pickup-ready — keeps the player:ready ACK
 * aligned with the "ready to throw" moment, not the "state changed"
 * moment.
 *
 * onUpdate: _checkPlayers fires the dice-result flyout when the
 * banker's result lands on update.players[i].result.
 *
 * onExit: pure choreography — playRollResolution (forfeit / dice
 * flyout / tie / wins), then runShooterHandoff if the active roller
 * differs from the next state's active player. Dice cleanup is owned
 * by the destination state's enterBettingMode, not this state.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class BankerRollState extends State {
  private _enterShooterId: string | null = null;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    this._enterShooterId = update.bankerId ?? null;
    this._resetRollSetup();

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();
    game.instructionBanner?.hide();

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this._updateProfiles(update);
    await this._checkPlayers(update);

    this._showOpponentReadyDice(update.bankerId, 3);

    // Authorization rides a follow-up update (server authorizes post-broadcast)
    // → handled from whichever of onEnter/onUpdate first sees us authorized.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    const game = this.game;
    this._updateProfiles(update);
    await this._checkPlayers(update);

    // A no-count roll keeps us in BANKER_ROLL: handleDiceStopped emits a
    // same-state update carrying the just-rolled dice (lastRollResult) and the
    // server stays put (no transition). Play the dice label here — spectators
    // get it from this update; the local banker already saw it via the local
    // dice-stop fire (deduped, no-op). Then, if it's MY no-count, await the
    // label and re-arm my pickup so I can re-roll in place. A scoring roll
    // transitions away (handled in onExit), so resultNeedsReroll guards it out.
    if (update.lastRollResult) {
      game.handleNewRollResult(update.lastRollResult);
      const localId = game.getLocalPlayerId();
      const mine = update.lastRollResult.playerId === localId;
      if (mine && game.resultNeedsReroll(update.lastRollResult.result)) {
        await game.playerProfilesOverlay?.waitForAllFlyouts();
        this._resetRollSetup();
      }
    }

    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    await this._checkPlayers(update);
    await this.game.playRollResolution(update);

    const next = update.bankerId ?? update.currentPlayerId ?? null;
    const prev = this._enterShooterId;
    if (prev && next && prev !== next) {
      await this.game.runShooterHandoff(update, prev, next);
    }

    // Pre-spawn the upcoming betting state's zones (PLAYER_BETTING fader
    // zone) before its camera lerp starts. Mirrors craps onExit calls.
    this.game.primeBetZones();
  }
}
